import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DS2 } from '../theme/designTokens';
import { AuthPageShell } from '../components/ui/AuthPageShell';
import { AuthBrandMark } from '../components/ui/AuthBrandMark';
import { AuthField } from '../components/ui/AuthField';
import { AuthSubmitButton } from '../components/ui/AuthSubmitButton';
import { AuthHeading } from '../components/ui/AuthHeading';
import { AuthBody } from '../components/ui/AuthBody';
import { PasswordVisibilityToggle } from '../components/ui/PasswordVisibilityToggle';
import { LOGIN_GLOW_1, LOGIN_GLOW_2 } from './LoginScreen';
import { createAccount, setDisplayName } from '../utils/firebase';
import { apiClient } from '../network/ApiClient';
import { getPendingSignup, setPendingSignup, clearPendingSignup } from '../utils/pendingSignup';
import { clearRegistrationStatusCache } from '../hooks/useRegistrationStatus';
import { useTranslation } from '../i18n/react';

/**
 * EMAIL REGISTRATION — Figma "Register with email 1" (Choose Your Email and
 * Password) / "Register with email 2" (Choose your Username), 2560×1321
 * export, values ×0.75 (same scale/glow geometry as Login — both frames
 * specify the identical Gradient+Blur values). Replaces the old 4-step
 * name/username/email/password/welcome wizard entirely; that flow is gone,
 * not just visually redone.
 *
 * Two-step wizard, ONE Firebase account:
 *   Step 1 (SignupCredentialsScreen, /signup/email) — client-side validates
 *     email format + password strength only. Does NOT touch Firebase yet
 *     (createUserWithEmailAndPassword needs the password too, which this
 *     step already has, but "email already in use" can only be discovered
 *     once account creation actually runs — see Step 2). Holds
 *     {email,password} in transient in-memory-only state (utils/
 *     pendingSignup — never localStorage/sessionStorage/URL/history) and
 *     navigates to Step 2.
 *   Step 2 (SignupUsernameScreen, /signup/username) — "Sign Up" creates the
 *     Firebase account (createAccount), THEN saves the username to the
 *     backend (PATCH /me/username). This ordering is why credentials must
 *     survive the navigation: Firebase account creation is genuinely
 *     deferred to this step, not Step 1.
 *
 * Partial-failure recovery: if Firebase succeeds but the backend username
 * save fails (network, or a genuine uniqueness race), the user is already
 * authenticated and stays on this exact screen — retry only re-attempts the
 * backend save, never createAccount() again (see the `accountReady` local
 * state below, and useRegistrationStatus/RequireAuth for what happens if
 * they instead navigate away and come back later).
 */

// ─── Step 1: email + password — Figma "Register with email 1" ─────────────

export function SignupCredentialsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  // Bounced back from Step 2 because Firebase reported this email is
  // already registered (only discoverable at actual account creation —
  // see SignupUsernameScreen). Email is prefilled for convenience; the
  // password is NEVER carried back here, so the field starts empty.
  const emailAlreadyInUse = (location.state as { emailAlreadyInUse?: string } | null)?.emailAlreadyInUse;

  const [email,    setEmail]    = useState(emailAlreadyInUse ?? '');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  // Translation KEY, not a rendered string — re-renders on language switch.
  const [emailError, setEmailError] = useState<'signup.emailInUse' | ''>(
    emailAlreadyInUse ? 'signup.emailInUse' : '',
  );

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  // Existing project convention (SignupPasswordScreen / ForgotPasswordNewScreen): >= 8 chars.
  const isValidPassword = password.length >= 8;
  const isValid = isValidEmail && isValidPassword;

  function handleNext(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    setEmailError('');
    setPendingSignup(email.trim(), password);
    navigate('/signup/username');
  }

  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={LOGIN_GLOW_2}>
      <AuthBrandMark />
      <AuthHeading>{t('signup.credentialsHeading')}</AuthHeading>

      <form onSubmit={handleNext} style={{ width: '100%', marginTop: 24, display: 'flex', flexDirection: 'column', gap: 15 }}>
        <AuthField
          label={t('login.email')}
          value={email}
          onChange={v => { setEmail(v); if (emailError) setEmailError(''); }}
          type="email"
          autoComplete="email"
          error={emailError ? t(emailError) : ''}
        />
        <AuthField
          label={t('login.password')}
          value={password}
          onChange={setPassword}
          type={showPw ? 'text' : 'password'}
          autoComplete="new-password"
          rightSlot={<PasswordVisibilityToggle visible={showPw} onToggle={() => setShowPw(v => !v)} field="passwordField" />}
        />

        <div style={{ marginTop: 3 }}>
          <AuthSubmitButton disabled={!isValid}>{tc('action.next')}</AuthSubmitButton>
        </div>
      </form>
    </AuthPageShell>
  );
}

// ─── Step 2: username — Figma "Register with email 2" ─────────────────────

type SignupErrorKey = 'signup.usernameTaken' | 'signup.usernameInvalid' | 'signup.errorGeneric' | '';

export function SignupUsernameScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const [username, setUsername] = useState('');
  const [error,     setError]   = useState<SignupErrorKey>('');
  const [loading,   setLoading] = useState(false);
  // Firebase account already exists for this attempt (either just created
  // this step, or we're resuming an incomplete registration) — a retry
  // after a backend-only failure must skip createAccount() and go straight
  // to the username save.
  const [accountReady, setAccountReady] = useState(false);
  // Non-null when the server reported this account already has a DIFFERENT
  // username than the one just submitted (a lost-response + different-name
  // retry). Holds the AUTHORITATIVE stored username to show the user; we do
  // NOT navigate away silently and do NOT imply the just-typed name saved.
  const [alreadySetTo, setAlreadySetTo] = useState<string | null>(null);

  const isValidUsername = username.trim().length >= 2 && username.trim().length <= 14;

  // Resuming an incomplete registration (already Firebase-authenticated,
  // no in-memory credentials — SignupStep2Guard already confirmed one of
  // these two is true) skips straight to "account exists, just need the
  // username save."
  useEffect(() => {
    if (!getPendingSignup()) setAccountReady(true);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading || !isValidUsername) return;
    setError('');
    setLoading(true);
    try {
      if (!accountReady) {
        const pending = getPendingSignup();
        if (!pending) {
          // Genuinely nothing to submit with (e.g. cleared by another tab) —
          // SignupStep2Guard normally catches this on mount, but handle the
          // mid-session edge case safely rather than throwing.
          navigate('/signup/email', { replace: true });
          return;
        }
        try {
          await createAccount(pending.email, pending.password);
        } catch (err: unknown) {
          const fe = err as { code?: string };
          if (fe.code === 'auth/email-already-in-use') {
            // Firebase is authoritative for email uniqueness; this can only be
            // discovered here. Bounce to Step 1 with the email preserved (via
            // its own state there — not carried through pendingSignup) and
            // the password cleared (never carried back, never preserved).
            clearPendingSignup();
            navigate('/signup/email', { state: { emailAlreadyInUse: pending.email } });
            return;
          }
          throw err; // network/other Firebase error → generic retry message below
        }
        setAccountReady(true);
        clearPendingSignup(); // no longer needed — the account exists now
      }

      const result = await apiClient.updateUsername(username.trim());
      if (result.ok) {
        await setDisplayName(username.trim()).catch(() => {}); // cosmetic sync only, never blocks completion
        clearRegistrationStatusCache();
        navigate('/home', { replace: true });
        return;
      }
      if (result.error === 'username_taken') {
        setError('signup.usernameTaken');
      } else if (result.error === 'invalid_username') {
        setError('signup.usernameInvalid');
      } else if (result.error === 'username_already_set') {
        // Set-once: a username was already stored for this account (a prior
        // submit succeeded but its response was lost, and the user retried
        // with a DIFFERENT name). Do NOT claim the new name saved — show the
        // actual stored username and let them Continue. The stored value is
        // authoritative (from the server body); the just-typed name is
        // discarded, never persisted.
        clearRegistrationStatusCache();
        setAlreadySetTo(result.username);
      } else {
        // 'unknown' (5xx / unexpected) — retryable. accountReady is already
        // true here, so retry only re-attempts the username save, never a
        // second Firebase account.
        setError('signup.errorGeneric');
      }
    } catch {
      // createAccount transport error, or updateUsername fetch rejection —
      // retryable, no second Firebase account (accountReady already true if
      // we got past account creation).
      setError('signup.errorGeneric');
    } finally {
      setLoading(false);
    }
  }

  // Set-once explanatory state: a username already existed for this account.
  // Necessary recovery UI (not a change to the normal Step 2 visuals) — same
  // Auth-family primitives as the rest of the flow.
  if (alreadySetTo) {
    return (
      <AuthPageShell glow1={LOGIN_GLOW_1} glow2={LOGIN_GLOW_2}>
        <AuthBrandMark />
        <AuthHeading>{t('signup.allSetHeading')}</AuthHeading>
        {/* The stored username is user data — interpolated verbatim. */}
        <AuthBody>{t('signup.alreadySet', { username: alreadySetTo })}</AuthBody>
        <div style={{ width: '100%', marginTop: 24 }}>
          <button
            type="button"
            onClick={() => { clearRegistrationStatusCache(); navigate('/home', { replace: true }); }}
            style={{
              width: '100%', height: 39, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
              justifyContent: 'center', borderRadius: 11.25, background: DS2.gradient.gold,
              border: '0.75px solid #C9A84C', boxShadow: '0px 22.5px 60px -22.5px rgba(201, 168, 76, 0.45)',
              color: '#FFFFFF', fontFamily: DS2.font.body, fontWeight: 500, fontSize: 16.5, cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {tc('action.continue')}
          </button>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={LOGIN_GLOW_2}>
      <AuthBrandMark />
      <AuthHeading>{t('signup.usernameHeading')}</AuthHeading>

      <form onSubmit={handleSubmit} style={{ width: '100%', marginTop: 24, display: 'flex', flexDirection: 'column', gap: 15 }}>
        <AuthField
          label={t('signup.username')}
          value={username}
          onChange={v => { setUsername(v); if (error) setError(''); }}
          autoComplete="username"
          error={error ? t(error) : ''}
        />

        <div style={{ marginTop: 3 }}>
          <AuthSubmitButton loading={loading} loadingLabel={t('signup.signingUp')} disabled={!isValidUsername}>
            {t('signup.submit')}
          </AuthSubmitButton>
        </div>
      </form>
    </AuthPageShell>
  );
}
